import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
    useArchiveProject,
    useArchivedProjects,
    useCreateProject,
    useProjects,
    useUpdateProject
} from '@/hooks/use-projects';
import { ApiError } from '@/modules/api/api-client';
import { useAuthStore } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';

import { EnvironmentManager } from '@/components/admin/environment-manager';

import type {
    CreateProjectRequest,
    ProjectListItem,
    UpdateProjectRequest,
} from '@/types/project';

// --- Validation helpers ---

function validateProjectKey(value: string): string | null {
  const upper = value.toUpperCase();
  if (!/^[A-Z0-9]{2,10}$/.test(upper)) {
    return 'Project key must be 2-10 uppercase alphanumeric characters';
  }
  return null;
}

function validateDisplayName(value: string): string | null {
  if (!value || value.trim().length === 0) {
    return 'Display name is required';
  }
  if (value.length > 100) {
    return 'Display name must be at most 100 characters';
  }
  return null;
}

// --- Types ---

type FormMode = 'create' | 'edit';

interface FormErrors {
  projectKey?: string;
  displayName?: string;
}

// --- Access Denied Component ---

function AccessDenied() {
  const navigate = useNavigate();

  return (
    <main id="main-content" className="max-w-4xl mx-auto px-4 py-6">
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <h1 className="mt-4 text-xl font-semibold text-content">Access Denied</h1>
        <p className="mt-2 text-sm text-content-secondary">
          You do not have permission to access the Project Administration page. Only administrators can manage projects.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          Go to Dashboard
        </button>
      </div>
    </main>
  );
}

// --- Confirmation Dialog ---

interface ConfirmDialogProps {
  projectName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isArchiving: boolean;
}

function ConfirmArchiveDialog({ projectName, onConfirm, onCancel, isArchiving }: ConfirmDialogProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-archive-title"
    >
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      <div className="relative bg-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4 border border-border">
        <h2 id="confirm-archive-title" className="text-lg font-semibold text-content mb-2">
          Archive Project
        </h2>
        <p className="text-sm text-content-secondary mb-6">
          Are you sure you want to archive <strong>{projectName}</strong>? This will hide the project from the default project list. This action can be reversed by an administrator.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isArchiving}
            className="px-4 py-2 text-sm font-medium rounded-md text-content-secondary bg-surface border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isArchiving}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 min-h-[44px]"
          >
            {isArchiving ? 'Archiving...' : 'Archive'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Project Form ---

interface ProjectFormProps {
  mode: FormMode;
  initialValues?: { projectKey: string; displayName: string };
  errors: FormErrors;
  isSubmitting: boolean;
  onSubmit: (values: { projectKey: string; displayName: string }) => void;
  onCancel: () => void;
}

function ProjectForm({ mode, initialValues, errors, isSubmitting, onSubmit, onCancel }: ProjectFormProps) {
  const [projectKey, setProjectKey] = useState(initialValues?.projectKey ?? '');
  const [displayName, setDisplayName] = useState(initialValues?.displayName ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ projectKey, displayName });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Project Key */}
      <div>
        <label htmlFor="project-key" className="block text-sm font-medium text-content-secondary mb-1">
          Project Key
          <span className="ml-1 text-xs text-content-secondary font-normal">
            (2-10 uppercase alphanumeric characters)
          </span>
        </label>
        {mode === 'edit' ? (
          <input
            id="project-key"
            type="text"
            value={projectKey}
            readOnly
            aria-readonly="true"
            className="w-full rounded-md border border-border bg-surface-raised px-3 py-2 text-sm text-content-secondary cursor-not-allowed min-h-[44px]"
          />
        ) : (
          <input
            id="project-key"
            type="text"
            value={projectKey}
            onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
            maxLength={10}
            placeholder="e.g. TR2, BEQ, ACME"
            aria-invalid={!!errors.projectKey}
            aria-describedby={errors.projectKey ? 'project-key-error' : undefined}
            className={`w-full rounded-md border px-3 py-2 text-sm text-content bg-surface min-h-[44px] focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
              errors.projectKey ? 'border-red-500' : 'border-border'
            }`}
          />
        )}
        {errors.projectKey && (
          <p id="project-key-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.projectKey}
          </p>
        )}
      </div>

      {/* Display Name */}
      <div>
        <label htmlFor="display-name" className="block text-sm font-medium text-content-secondary mb-1">
          Display Name
          <span className="ml-1 text-xs text-content-secondary font-normal">
            (max 100 characters)
          </span>
        </label>
        <input
          id="display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={100}
          placeholder="e.g. Tier 2 Platform"
          aria-invalid={!!errors.displayName}
          aria-describedby={errors.displayName ? 'display-name-error' : undefined}
          className={`w-full rounded-md border px-3 py-2 text-sm text-content bg-surface min-h-[44px] focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
            errors.displayName ? 'border-red-500' : 'border-border'
          }`}
        />
        {errors.displayName && (
          <p id="display-name-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.displayName}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium rounded-md text-content-secondary bg-surface border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
        >
          {isSubmitting
            ? mode === 'create' ? 'Creating...' : 'Saving...'
            : mode === 'create' ? 'Create Project' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

// --- Main Page ---

export function ProjectAdminPage() {
  const user = useAuthStore((state) => state.user);
  const addToast = useToastStore((state) => state.addToast);

  // Check admin access
  const isAdmin = user?.role === 'Admin';

  // UI state
  const [showArchived, setShowArchived] = useState(false);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [editingProject, setEditingProject] = useState<ProjectListItem | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [archiveTarget, setArchiveTarget] = useState<ProjectListItem | null>(null);

  // Data fetching
  const { data: projectsData, isLoading: isLoadingProjects } = useProjects();
  const { data: archivedData, isLoading: isLoadingArchived } = useArchivedProjects();

  // Mutations
  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();
  const archiveMutation = useArchiveProject();

  // If not admin, show access denied
  if (!isAdmin) {
    return <AccessDenied />;
  }

  const activeProjects = projectsData?.projects ?? [];
  const archivedProjects = archivedData?.projects ?? [];

  const displayedProjects = showArchived
    ? [...activeProjects, ...archivedProjects]
    : activeProjects;

  // --- Handlers ---

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingProject(null);
    setFormErrors({});
  };

  const handleOpenEdit = (project: ProjectListItem) => {
    setFormMode('edit');
    setEditingProject(project);
    setFormErrors({});
  };

  const handleCloseForm = () => {
    setFormMode(null);
    setEditingProject(null);
    setFormErrors({});
  };

  const handleCreateSubmit = (values: { projectKey: string; displayName: string; }) => {
    // Client-side validation
    const errors: FormErrors = {};
    const keyError = validateProjectKey(values.projectKey);
    if (keyError) errors.projectKey = keyError;
    const nameError = validateDisplayName(values.displayName);
    if (nameError) errors.displayName = nameError;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const req: CreateProjectRequest = {
      projectKey: values.projectKey.toUpperCase(),
      displayName: values.displayName,
    };

    createMutation.mutate(req, {
      onSuccess: () => {
        addToast({ type: 'success', message: 'Project created successfully.', duration: 5000 });
        handleCloseForm();
      },
      onError: (error: unknown) => {
        if (error instanceof ApiError) {
          if (error.status === 409) {
            const body = error.body as { error?: string; message?: string; field?: string } | undefined;
            if (body?.field === 'projectKey' || body?.error === 'duplicate_key') {
              setFormErrors({ projectKey: body?.message ?? 'A project with this key already exists' });
              return;
            }
          }
          if (error.status === 400) {
            const body = error.body as { field?: string; message?: string } | undefined;
            if (body?.field && body?.message) {
              setFormErrors({ [body.field]: body.message } as FormErrors);
              return;
            }
          }
        }
        addToast({ type: 'error', message: 'Failed to create project. Please try again.', duration: 8000 });
      },
    });
  };

  const handleEditSubmit = (values: { projectKey: string; displayName: string }) => {
    if (!editingProject) return;

    // Client-side validation (key is read-only, skip it)
    const errors: FormErrors = {};
    const nameError = validateDisplayName(values.displayName);
    if (nameError) errors.displayName = nameError;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const req: UpdateProjectRequest = {
      displayName: values.displayName,
    };

    updateMutation.mutate(
      { key: editingProject.projectKey, req },
      {
        onSuccess: () => {
          addToast({ type: 'success', message: 'Project updated successfully.', duration: 5000 });
          handleCloseForm();
        },
        onError: (error: unknown) => {
          if (error instanceof ApiError && error.status === 400) {
            const body = error.body as { field?: string; message?: string } | undefined;
            if (body?.field && body?.message) {
              setFormErrors({ [body.field]: body.message } as FormErrors);
              return;
            }
          }
          addToast({ type: 'error', message: 'Failed to update project. Please try again.', duration: 8000 });
        },
      },
    );
  };

  const handleArchiveConfirm = () => {
    if (!archiveTarget) return;

    archiveMutation.mutate(archiveTarget.projectKey, {
      onSuccess: () => {
        addToast({ type: 'success', message: `Project "${archiveTarget.displayName}" archived successfully.`, duration: 5000 });
        setArchiveTarget(null);
      },
      onError: () => {
        addToast({ type: 'error', message: 'Failed to archive project. Please try again.', duration: 8000 });
        setArchiveTarget(null);
      },
    });
  };

  return (
    <main id="main-content" className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-content">Project Administration</h1>
        {!formMode && (
          <button
            type="button"
            onClick={handleOpenCreate}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Project
          </button>
        )}
      </div>

      {/* Create/Edit Form */}
      {formMode && (
        <section
          className="bg-surface rounded-lg p-6 shadow-sm border border-border mb-6"
          aria-labelledby="form-heading"
        >
          <h2 id="form-heading" className="text-lg font-semibold text-content mb-4">
            {formMode === 'create' ? 'Create New Project' : `Edit Project: ${editingProject?.displayName}`}
          </h2>
          <ProjectForm
            mode={formMode}
            initialValues={
              formMode === 'edit' && editingProject
                ? {
                    projectKey: editingProject.projectKey,
                    displayName: editingProject.displayName,
                  }
                : undefined
            }
            errors={formErrors}
            isSubmitting={formMode === 'create' ? createMutation.isPending : updateMutation.isPending}
            onSubmit={formMode === 'create' ? handleCreateSubmit : handleEditSubmit}
            onCancel={handleCloseForm}
          />
          {formMode === 'edit' && editingProject && (
            <EnvironmentManager projectKey={editingProject.projectKey} />
          )}
        </section>
      )}

      {/* Show/Hide Archived Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-content">Projects</h2>
        <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700"
          />
          <span className="text-sm text-content-secondary">Show archived projects</span>
        </label>
      </div>

      {/* Loading State */}
      {(isLoadingProjects || (showArchived && isLoadingArchived)) && (
        <div className="space-y-3 animate-pulse" aria-label="Loading projects">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface rounded-lg p-4 border border-border">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {/* Project List */}
      {!isLoadingProjects && (
        <>
          {displayedProjects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-content-secondary">
                No projects found. Create your first project to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-3" role="list" aria-label="Project list">
              {displayedProjects.map((project) => (
                <div
                  key={project.projectKey}
                  role="listitem"
                  className={`bg-surface rounded-lg p-4 border shadow-sm ${
                    project.archived
                      ? 'border-border opacity-60'
                      : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-200">
                          {project.projectKey}
                        </span>
                        <h3 className="text-sm font-medium text-content truncate">
                          {project.displayName}
                        </h3>
                        {project.archived && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-xs text-content-secondary">
                        <span>{project.repositoryCount} {project.repositoryCount === 1 ? 'repository' : 'repositories'}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    {!project.archived && (
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(project)}
                          className="px-3 py-1.5 text-xs font-medium rounded-md text-content-secondary border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[36px]"
                          aria-label={`Edit project ${project.displayName}`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setArchiveTarget(project)}
                          className="px-3 py-1.5 text-xs font-medium rounded-md text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 min-h-[36px]"
                          aria-label={`Archive project ${project.displayName}`}
                        >
                          Archive
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Archive Confirmation Dialog */}
      {archiveTarget && (
        <ConfirmArchiveDialog
          projectName={archiveTarget.displayName}
          onConfirm={handleArchiveConfirm}
          onCancel={() => setArchiveTarget(null)}
          isArchiving={archiveMutation.isPending}
        />
      )}
    </main>
  );
}
